import React, { useRef, useState } from "react";
import { FaChevronDown, FaTrash, FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import refundHeroBox from "../../assets/images/refund/refund-hero-box.png";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { uploadCmsPageImage } from "../../services/cmsUpload.service";
import { CMS_HERO_MAX_BYTES, prepareCmsHeroImage } from "../../utils/prepareCmsHeroImage";
import { DEFAULT_REFUND_CMS_CONFIG } from "../../utils/refundPageCms";
import RichTextEditor from "./RichTextEditor";
import "../../assets/styles/cms_contact_editor.scss";

function Field({ label, children, hint }) {
  return (
    <div className="cms-contact-editor__field">
      <label className="cms-contact-editor__label">{label}</label>
      {children}
      {hint ? <p className="cms-contact-editor__hint">{hint}</p> : null}
    </div>
  );
}

function VisibilityToggle({ checked, disabled, onChange, label }) {
  return (
    <div className="cms-visibility-toggle">
      <label className={`cms-on-off${checked ? " is-on" : " is-off"}`} title={label}>
        <input
          type="checkbox"
          className="cms-on-off__input"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          aria-label={label}
        />
        <span className="cms-on-off__track" aria-hidden>
          <span className="cms-on-off__knob" />
        </span>
      </label>
      <span
        className={`cms-visibility-toggle__label${
          checked ? " cms-visibility-toggle__label--on" : " cms-visibility-toggle__label--off"
        }`}
      >
        {checked ? "Visible" : "Hidden"}
      </span>
    </div>
  );
}

const ALLOWED_EXT = new Set(["jpg", "jpeg", "png", "webp"]);
const MAX_INPUT_BYTES = 8 * 1024 * 1024;

export default function RefundCmsEditor({
  config,
  onChange,
  readOnly = false,
  effectiveDate = "",
  onEffectiveDateChange,
}) {
  const data = config || DEFAULT_REFUND_CMS_CONFIG;
  const sections = Array.isArray(data.sections) ? data.sections : DEFAULT_REFUND_CMS_CONFIG.sections;
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [expandedId, setExpandedId] = useState(sections[0]?.id || null);

  const patch = (partial) => onChange({ ...data, ...partial });

  const patchSection = (sectionId, partial) => {
    if (readOnly) return;
    patch({
      sections: sections.map((section) =>
        section.id === sectionId ? { ...section, ...partial } : section,
      ),
    });
  };

  const validateFile = (file) => {
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED_EXT.has(ext)) {
      toast.error("Only JPG, JPEG, PNG and WEBP images are allowed.");
      return false;
    }
    if (file.size > MAX_INPUT_BYTES) {
      toast.error("Source image must be 8 MB or smaller.");
      return false;
    }
    return true;
  };

  const handleBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || readOnly || !validateFile(file)) return;

    setUploading(true);
    try {
      const prepared = await prepareCmsHeroImage(file, {
        maxBytes: CMS_HERO_MAX_BYTES,
        removeBackground: true,
        fileNamePrefix: "refund-hero",
      });
      const imagePath = await uploadCmsPageImage(prepared, { fileType: "refund_hero_illustration" });
      patch({ heroBackgroundUrl: imagePath });
      toast.success(
        `Hero image processed (${Math.ceil(prepared.size / 1024)} KB). Click Save to publish.`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Hero image upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const clearBackground = () => {
    if (readOnly) return;
    patch({ heroBackgroundUrl: "" });
  };

  const hasCustomBackground = Boolean(data.heroBackgroundUrl);
  const previewUrl = hasCustomBackground
    ? resolvePublicFileUrl(data.heroBackgroundUrl)
    : refundHeroBox;
  const visibleCount = sections.filter((section) => section.visible !== false).length;

  return (
    <div className="cms-contact-editor">
      <p className="cms-contact-editor__intro">
        Configure the <strong>Refund &amp; Cancellation Policy</strong> page at{" "}
        <strong>/refund-cancellation-policy</strong>. Edit the hero image, effective date, section
        visibility, and each policy&apos;s main content. Click <strong>Save</strong> to publish.
      </p>

      <section className="cms-contact-editor__section">
        <h3 className="cms-contact-editor__section-title">Hero</h3>
        <Field
          label="Hero image"
          hint="Background is removed automatically and the file is compressed to 200 KB or less. JPG, PNG, or WEBP."
        >
          <div className="cms-contact-editor__upload-row">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              hidden
              disabled={readOnly || uploading}
              onChange={handleBackgroundUpload}
            />
            <button
              type="button"
              className="cms-contact-editor__add-btn"
              disabled={readOnly || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <FaUpload aria-hidden />
              {uploading ? "Uploading…" : "Upload image"}
            </button>
            {hasCustomBackground ? (
              <button
                type="button"
                className="cms-contact-editor__delete-btn"
                disabled={readOnly || uploading}
                onClick={clearBackground}
              >
                <FaTrash aria-hidden />
                Reset to default
              </button>
            ) : null}
          </div>
        </Field>
        <div className="cms-contact-editor__preview">
          <img
            src={previewUrl}
            alt={
              hasCustomBackground
                ? "Refund Policy hero image preview"
                : "Default Refund Policy hero graphic"
            }
          />
        </div>
        {!hasCustomBackground ? (
          <p className="cms-contact-editor__hint">
            Showing the default hero graphic used on `/refund-cancellation-policy`.
          </p>
        ) : null}

        {typeof onEffectiveDateChange === "function" ? (
          <Field
            label="Effective date"
            hint="Shown in the hero next to Last Updated."
          >
            <input
              type="date"
              className="cms-contact-editor__input"
              value={effectiveDate || ""}
              disabled={readOnly}
              onChange={(e) => onEffectiveDateChange(e.target.value)}
            />
          </Field>
        ) : null}
      </section>

      <section className="cms-contact-editor__section">
        <div className="cms-contact-editor__emails-head">
          <h3 className="cms-contact-editor__section-title" style={{ margin: 0 }}>
            Policy sections
          </h3>
          <span className="cms-contact-editor__hint" style={{ margin: 0 }}>
            {visibleCount} of {sections.length} visible
          </span>
        </div>
        <p className="cms-contact-editor__hint" style={{ marginBottom: 12 }}>
          Expand a section to edit its title and main content (6 policy sections). Use Visible /
          Hidden to control whether it appears on the public page.
        </p>
        <div className="cms-privacy-visibility-list">
          {sections.map((section, index) => {
            const isVisible = section.visible !== false;
            const isExpanded = expandedId === section.id;
            return (
              <div
                key={section.id}
                className={`cms-privacy-section-card${isVisible ? "" : " is-hidden"}${
                  isExpanded ? " is-expanded" : ""
                }`}
              >
                <div className="cms-privacy-section-card__head">
                  <button
                    type="button"
                    className="cms-privacy-section-card__expand"
                    aria-expanded={isExpanded}
                    onClick={() => setExpandedId(isExpanded ? null : section.id)}
                  >
                    <span className="cms-privacy-visibility-row__num">{index + 1}</span>
                    <span className="cms-privacy-section-card__title">{section.title}</span>
                    <FaChevronDown
                      className={`cms-privacy-section-card__chevron${isExpanded ? " is-open" : ""}`}
                      aria-hidden
                    />
                  </button>
                  <VisibilityToggle
                    checked={isVisible}
                    disabled={readOnly}
                    onChange={(checked) => patchSection(section.id, { visible: checked })}
                    label={`${section.title}: ${isVisible ? "Visible" : "Hidden"}`}
                  />
                </div>

                {isExpanded ? (
                  <div className="cms-privacy-section-card__body">
                    <Field label="Title">
                      <input
                        type="text"
                        className="cms-contact-editor__input"
                        value={section.title || ""}
                        disabled={readOnly}
                        onChange={(e) => patchSection(section.id, { title: e.target.value })}
                      />
                    </Field>
                    <Field label="Main content" hint="Policy body shown when the section is opened.">
                      <RichTextEditor
                        value={section.body || ""}
                        readOnly={readOnly}
                        placeholder="Write the policy section content…"
                        onChange={(html) => {
                          if (readOnly) return;
                          patchSection(section.id, { body: html });
                        }}
                      />
                    </Field>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
